/**
 * DatasetRecordsTable — a NuclearnDataTable wrapper for browsing raw dataset
 * records (work orders, utility PM catalog rows, …) with the same feel as the
 * Location Overview work-order / condition-report tables:
 *
 *   - config-driven *default* visible columns, with a show/hide-columns picker
 *     so the user can reveal any other field on the record ("select what columns
 *     you want viewable");
 *   - every row expands to an all-fields detail panel (the full raw record),
 *     with an optional caller-supplied header (e.g. the AI selection_reason for
 *     a work order).
 *
 * Kept generic so both the EPRI-task work-order drill-down and the orphan
 * utility-PM table share one implementation.
 */

import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import type { MRT_ColumnDef } from "material-react-table";

import NuclearnDataTable from "src/reusable/NuclearnDataTable/NuclearnDataTable";

// A record row always carries its raw `data` blob; callers may attach extra
// typed fields (wo_id, confidence, …) alongside for use in extraColumns /
// renderExtraDetail (accessed through the concrete row type T, not this base).
export interface DatasetRecordRow {
  data?: Record<string, unknown> | null;
}

interface DatasetRecordsTableProps<T extends DatasetRecordRow> {
  records: T[];
  /** Columns shown by default. The rest are hidden but toggleable from the picker. */
  visibleColumns?: string[] | null;
  /** Pinned to the front of the column order (and shown by default). */
  idColumn?: string | null;
  dateColumn?: string | null;
  isLoading?: boolean;
  emptyMessage?: string;
  tableTestId: string;
  getRowId?: (row: T) => string;
  /** Extra leading columns (e.g. a confidence chip) rendered before the data columns. */
  extraColumns?: MRT_ColumnDef<T>[];
  /** Extra detail-panel content rendered above the raw fields (e.g. AI reason). */
  renderExtraDetail?: (row: T) => React.ReactNode;
  initialPageSize?: number;
  /** Client-side pagination. Off when an outer pager owns the page (e.g. ESOMS). */
  enablePagination?: boolean;
}

export const prettifyColumnName = (name: string): string =>
  name
    .replace(/__/g, " > ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Detail panel: the whole raw record as label/value rows, id + date pinned to
// the top, everything else alphabetical — mirrors the Overview WO/CR panels.
// Exported so the utility-PM roll-up rows can reuse it for their MX_PM record.
export const RecordFieldsPanel: React.FC<{
  data: Record<string, unknown>;
  idColumn?: string | null;
  dateColumn?: string | null;
  header?: React.ReactNode;
}> = ({ data, idColumn, dateColumn, header }) => {
  const fields = useMemo(() => {
    const priorityOf = (key: string) =>
      key === idColumn ? 0 : key === dateColumn ? 1 : 2;
    return Object.entries(data || {}).sort(([a], [b]) => {
      const pa = priorityOf(a);
      const pb = priorityOf(b);
      return pa !== pb ? pa - pb : a.localeCompare(b);
    });
  }, [data, idColumn, dateColumn]);

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
      {header}
      <Table size="small">
        <TableBody>
          {fields.map(([key, value]) => (
            <TableRow key={key}>
              <TableCell
                component="th"
                sx={{
                  fontWeight: 500,
                  width: "30%",
                  verticalAlign: "top",
                  borderBottom: "1px solid #eee",
                  backgroundColor: "#f5f5f5",
                }}
              >
                {prettifyColumnName(key)}
              </TableCell>
              <TableCell
                sx={{
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  borderBottom: "1px solid #eee",
                }}
              >
                {formatCellValue(value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const DatasetRecordsTable = <T extends DatasetRecordRow>({
  records,
  visibleColumns,
  idColumn,
  dateColumn,
  isLoading = false,
  emptyMessage = "No records.",
  tableTestId,
  getRowId,
  extraColumns = [],
  renderExtraDetail,
  initialPageSize = 10,
  enablePagination = true,
}: DatasetRecordsTableProps<T>) => {
  // Column order: id, then date, then the configured visible columns, then any
  // remaining keys seen across the records — alphabetical within each group.
  const orderedKeys = useMemo(() => {
    const seen = new Set<string>();
    // Seed with the known config columns so headers render before the records
    // arrive (and stay stable across the async load).
    if (idColumn) seen.add(idColumn);
    if (dateColumn) seen.add(dateColumn);
    for (const k of visibleColumns ?? []) seen.add(k);
    for (const r of records)
      for (const k of Object.keys(r.data ?? {})) seen.add(k);
    const all = [...seen];
    const rank = (k: string): number => {
      if (k === idColumn) return 0;
      if (k === dateColumn) return 1;
      const vi = visibleColumns?.indexOf(k) ?? -1;
      if (vi >= 0) return 2 + vi;
      return 1000;
    };
    return all.sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      return ra !== rb ? ra - rb : a.localeCompare(b);
    });
  }, [records, idColumn, dateColumn, visibleColumns]);

  const dataColumns = useMemo<MRT_ColumnDef<T>[]>(
    () =>
      orderedKeys.map((key) => ({
        id: key,
        accessorFn: (row: T) => row.data?.[key],
        header: prettifyColumnName(key),
        size: 180,
        Cell: ({ cell }) => {
          const text = formatCellValue(cell.getValue<unknown>());
          return (
            <Tooltip title={text} arrow enterDelay={600}>
              <Box
                sx={{
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {text}
              </Box>
            </Tooltip>
          );
        },
      })),
    [orderedKeys],
  );

  const columns = useMemo<MRT_ColumnDef<T>[]>(
    () => [...extraColumns, ...dataColumns],
    [extraColumns, dataColumns],
  );

  // Default visibility: the configured columns are shown, plus the id + date
  // columns always (so the primary identifier is never hidden even if a config's
  // visibleColumns omits it); everything else starts hidden but is toggleable
  // from the picker.
  const columnVisibility = useMemo(() => {
    const defaults = new Set<string>(
      visibleColumns && visibleColumns.length > 0 ? visibleColumns : [],
    );
    if (idColumn) defaults.add(idColumn);
    if (dateColumn) defaults.add(dateColumn);
    const vis: Record<string, boolean> = {};
    for (const key of orderedKeys)
      vis[key] = defaults.size === 0 || defaults.has(key);
    return vis;
  }, [orderedKeys, visibleColumns, idColumn, dateColumn]);

  return (
    <NuclearnDataTable<T>
      // Remount when the column set first materializes (records load async) so
      // the default column visibility below is applied to the full set — MRT
      // only reads initialState once per mount.
      key={orderedKeys.join("|")}
      tableTestId={tableTestId}
      columns={columns}
      data={records}
      getRowId={getRowId}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      enableHiding
      enableTopToolbar
      enableColumnActions
      enableGlobalFilter
      enablePagination={enablePagination}
      enableBottomToolbar={enablePagination}
      pageSizeOptions={[10, 25, 50]}
      initialState={{
        columnVisibility,
        // Default-sort newest-first by the configured date column so records
        // read chronologically (ISO date strings sort lexicographically =
        // chronologically). No date column → leave in source order.
        ...(dateColumn ? { sorting: [{ id: dateColumn, desc: true }] } : {}),
        pagination: { pageIndex: 0, pageSize: initialPageSize },
        density: "compact",
      }}
      getRowCanExpand={() => true}
      renderDetailPanel={({ row }) => (
        <RecordFieldsPanel
          data={row.original.data ?? {}}
          idColumn={idColumn}
          dateColumn={dateColumn}
          header={renderExtraDetail?.(row.original)}
        />
      )}
    />
  );
};

export default DatasetRecordsTable;
